import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './IdentifierGame.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Pick `n` random items from an array without repeats */
function sample(arr, n) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

/** Build one round: pick a correct item, then 5 distractors, shuffle all 6.
 *  If correctItem has a fixed `options` array (incorrect answers), use those;
 *  otherwise fall back to randomly sampling from the items pool. */
function buildRound(items, correctItem) {
  const distractors = correctItem.options
    ? correctItem.options.map(o => o.name)          // use pre-defined wrong answers
    : sample(items.filter(i => i.name !== correctItem.name), 5).map(i => i.name);
  return [...distractors, correctItem.name].sort(() => Math.random() - 0.5);
}

// ── Component ─────────────────────────────────────────────────────────────────
function IdentifierGame({ category }) {
  const navigate = useNavigate();
  const { title, question, subtitle, accent, bgColor, items } = category;

  // Initialize with a shuffled pool of items to ensure no repeats until all are shown
  const [itemPool, setItemPool] = useState(() => {
    return [...items].sort(() => Math.random() - 0.5);
  });
  const [currentIndex, setCurrentIndex] = useState(0);

  // Both current item and choices are derived from the SAME picked item to
  // guarantee the image and the answer options always match.
  const [round, setRound] = useState(() => {
    const item = itemPool[0];
    return { current: item, choices: buildRound(items, item) };
  });
  const { current, choices } = round;

  const [selected, setSelected] = useState(null);   // name chosen by user
  const [score,    setScore]    = useState({ correct: 0, total: 0 });
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ── Pick the best calm female voice available ─────────────────────────────
  const getFemaleVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const preferred = [
      'Google UK English Female',
      'Microsoft Zira - English (United States)',
      'Microsoft Libby Online (Natural) - English (United Kingdom)',
      'Microsoft Aria Online (Natural) - English (United States)',
      'Samantha',
      'Karen',
      'Moira',
    ];
    for (const name of preferred) {
      const v = voices.find(v => v.name === name);
      if (v) return v;
    }
    return (
      voices.find(v => /female/i.test(v.name) && /en[-_]/i.test(v.lang)) ||
      voices.find(v => /female/i.test(v.name)) ||
      voices.find(v => /en[-_]/i.test(v.lang)) ||
      null
    );
  };

  const speakText = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const speak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      const voice = getFemaleVoice();
      if (voice) utterance.voice = voice;
      utterance.lang   = 'en-US';
      utterance.rate   = 0.5;
      utterance.pitch  = 1.05;
      utterance.volume = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend   = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    };
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        speak();
      };
    } else {
      speak();
    }
  };

  const isAnswered = selected !== null;
  const isCorrect  = selected === current.name;

  const handleChoice = (name) => {
    if (isAnswered) return;
    setSelected(name);
    setScore(s => ({
      correct: s.correct + (name === current.name ? 1 : 0),
      total:   s.total + 1,
    }));
    // Speak the correct answer
    speakText(current.name);
  };

  const nextRound = () => {
    let nextIndex = currentIndex + 1;
    let nextPool = itemPool;

    // If we've shown all items, reshuffle and start over
    if (nextIndex >= itemPool.length) {
      nextPool = [...items].sort(() => Math.random() - 0.5);
      nextIndex = 0;
      setItemPool(nextPool);
    }

    const nextItem = nextPool[nextIndex];
    setRound({ current: nextItem, choices: buildRound(items, nextItem) });
    setCurrentIndex(nextIndex);
    setSelected(null);
  };

  return (
    <div className="ig-shell">
      {/* Header */}
      <header className="ig-header">
        <button className="ig-back-btn" onClick={() => navigate('/')}>← Home</button>
        <h1 className="ig-title">{title}</h1>
        <div className="ig-score" style={{ color: accent }}>
          ⭐ {score.correct} / {score.total}
        </div>
      </header>

      {/* Card */}
      <div className="ig-card">
        {/* Image display */}
        <div className="ig-img-wrap" style={{ background: bgColor }}>
          <img
            key={current.name}
            src={current.img}
            alt={current.name}
            className="ig-item-img"
            draggable={false}
          />
        </div>

        {/* Question */}
        <h2 className="ig-question">{question}</h2>
        <p  className="ig-subtitle">{subtitle}</p>

        {/* Choice grid */}
        <div className="ig-choices">
          {choices.map(name => {
            const isThis    = name === current.name;
            const isPicked  = name === selected;
            let state = '';
            if (isAnswered) {
              if (isThis)          state = 'correct';
              else if (isPicked)   state = 'wrong';
            }
            return (
              <button
                key={name}
                className={`ig-choice ${state} ${isPicked ? 'picked' : ''}`}
                onClick={() => handleChoice(name)}
                disabled={isAnswered}
              >
                {name}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {isAnswered && (
          <div className={`ig-feedback ${isCorrect ? 'ig-fb-correct' : 'ig-fb-wrong'}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="ig-fb-msg">
                {isCorrect
                  ? `🎉 Correct! That's a ${current.name}!`
                  : `❌ Oops! It was a ${current.name}.`}
              </span>
              <button
                className={`ig-audio-btn ${isSpeaking ? 'speaking' : ''}`}
                onClick={() => speakText(current.name)}
                title={isSpeaking ? 'Speaking…' : 'Read aloud'}
                disabled={isSpeaking}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isSpeaking ? 'not-allowed' : 'pointer',
                  fontSize: '24px',
                  padding: '0',
                }}
              >
                {isSpeaking ? '🔈' : '🔊'}
              </button>
            </div>
            <button
              className="ig-next-btn"
              style={{ background: accent }}
              onClick={nextRound}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default IdentifierGame;
